import { setContentEditorSessionConfirmed } from "@stores/contentStore";
import { fetchData } from "@utility/webServices";
import { getActivePinia } from "pinia";
import { isContentDesignerFrame } from "./contentDesignerProtocol";

interface IContentEditingSession {
  IsValid: boolean;
  DesignerOrigin: string;
}

const APP_START_GRACE_MS = 3000;
const APP_START_POLL_MS = 50;

/** Present only in FrontendRoutedPage.html, whose master templates mount placements themselves. */
const VUE_ROOT_ID = "s1";

/**
 * Waits for a Vue app to have claimed an active pinia, up to a short grace period.
 *
 * index.ts calls into here while the entry bundle evaluates, so the DOMContentLoaded handler below is
 * registered before the one FrontendRoutedPage.html writes at the end of the body -- and therefore runs
 * first, before startS1App. An api call at that moment has no app store to read the api key from, and
 * reading one anyway threw. Server-rendered pages build their app from a separate async chunk and some
 * never build one at all, so this is a grace period rather than a precondition: the work below goes
 * ahead either way once it lapses.
 */
const waitForAppStart = async (): Promise<void> => {
  const deadline = Date.now() + APP_START_GRACE_MS;
  while (!getActivePinia() && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, APP_START_POLL_MS));
  }
};

const onDocumentReady = (run: () => void): void => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
    return;
  }
  run();
};

/**
 * Asks the server whether this really is an editing session, and which origin may drive it.
 *
 * Nothing in the URL is trusted. The page arrives here having already been redirected by
 * HtmlPageHandler, which spent the one-time handoff and set HttpOnly session cookies; all that is
 * left in the address is the designer flag, which is not authorization. Without this call any site
 * could frame a public brand page, raise that flag, name itself as the parent, and have the bridge
 * render whatever html it sent through v-html on the brand origin against the visitor's session.
 *
 * The origin comes back from the server too, so the framed page never has to believe a query
 * parameter about who its parent is.
 */
const startWhenSessionIsValid = async (): Promise<void> => {
  await waitForAppStart();

  const session = await fetchData<IContentEditingSession>("ContentApi/start-content-editing-session", {});

  if (!session?.IsValid || !(session.DesignerOrigin ?? "").trim()) {
    return;
  }

  //Server confirmed, so the page may now offer its own content-editing affordances.
  setContentEditorSessionConfirmed(true);

  const client = await import("./contentDesignerClient");
  client.startContentDesignerClient(session.DesignerOrigin);
};

/**
 * Starts the designer bridge on any page that loads this bundle, not only the Vue-routed ones.
 * Server-rendered pages render through SiteMaster and never call startS1App, so hooking app startup
 * alone leaves them framed but unreachable.
 */
export const tryStartContentDesigner = (): void => {
  if (typeof window === "undefined" || window.parent === window) {
    return;
  }

  //Cheap reject only, so an ordinary visitor never issues the call below. It proves nothing: both
  //halves of it are things a page on this origin can set for itself, and the server decides.
  if (!isContentDesignerFrame()) {
    return;
  }

  onDocumentReady(() => {
    void startWhenSessionIsValid();
  });
};

/**
 * Mounts published selector-placed content on pages the Vue router does not own.
 */
export const tryStartContentPlacements = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  onDocumentReady(() => {
    if (document.getElementById(VUE_ROOT_ID)) {
      return;
    }

    void (async () => {
      await waitForAppStart();
      const runtime = await import("../contentPlacementRuntime");
      runtime.startContentPlacementRuntime();
    })();
  });
};
